/**
 * LossReasonsSettings — motivos de perda.
 * Mobile: toolbar Contatos-like. Desktop intacto.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { PageHeader } from '../../../components/layout/Header';
import { useLossReasonsPage } from './hooks/useLossReasonsPage';
import LossReasonTable from './components/LossReasonTable';
import LossReasonFormModal from './components/LossReasonFormModal';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import ActionMenu from '../../../components/shared/ActionMenu';
import SearchInput from '../../../components/ui/SearchInput';
import { Button } from '../../../components/ui/Button';
import BulkActionsBar from '../../../components/shared/BulkActionsBar';
import Pagination from '../../../components/shared/Pagination';

const LossReasonsSettings: React.FC = () => {
    const {
        filteredReasons,
        loading,
        searchTerm,
        setSearchTerm,
        selectedIds,
        reasonToEdit,
        reasonToDelete,
        isFormModalOpen,
        setIsFormModalOpen,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        isBulkDeleteModalOpen,
        setIsBulkDeleteModalOpen,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleOpenDeleteModal,
        handleSaveReason,
        handleConfirmDelete,
        handleBulkDelete,
        handleDuplicate,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        headerActions,
        bulkActions,
    } = useLossReasonsPage();

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const paginatedReasons = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * pageSize;
        return filteredReasons.slice(firstPageIndex, firstPageIndex + pageSize);
    }, [currentPage, filteredReasons, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    return (
        <div className="flex h-full flex-col">
            <PageHeader
                title="Motivos de Perda"
                subtitle="Cadastre os motivos pelos quais as negociações são perdidas."
                className="mb-0 gap-s3 md:mb-6 md:gap-3"
                titleClassName="text-lg md:text-xl"
                actionClassName="w-full md:w-auto"
                hideBackButton
                action={
                    <>
                        <div className="flex w-full flex-col gap-s3 md:hidden">
                            <div
                                role="toolbar"
                                aria-label="Ações de motivos de perda"
                                className="inline-flex h-11 w-fit items-stretch overflow-hidden rounded-xl border border-border bg-card"
                            >
                                <ActionMenu
                                    actions={headerActions}
                                    buttonClassName="h-11 w-11 rounded-none border-0 bg-transparent p-0 text-default shadow-none hover:bg-surface-subtle hover:text-primary"
                                />
                            </div>
                            <SearchInput
                                value={searchTerm}
                                onChange={setSearchTerm}
                                placeholder="Pesquisar motivos..."
                                className="min-h-11 w-full"
                            />
                            <Button
                                type="button"
                                onClick={handleOpenCreateModal}
                                className="min-h-11 w-full touch-manipulation"
                            >
                                Novo Motivo de Perda
                            </Button>
                        </div>
                        <div className="hidden md:flex md:items-center md:gap-2.5">
                            <div className="w-[250px] shrink-0">
                                <SearchInput
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    placeholder="Pesquisar motivos..."
                                    className="h-[36px]"
                                />
                            </div>
                            <Button type="button" onClick={handleOpenCreateModal}>
                                Novo Motivo de Perda
                            </Button>
                            <ActionMenu actions={headerActions} />
                        </div>
                    </>
                }
            />

            <section aria-label="Lista de motivos de perda" className="overflow-x-auto pt-s3 md:pt-0">
                <LossReasonTable
                    reasons={paginatedReasons}
                    isLoading={loading}
                    onEdit={handleOpenEditModal}
                    onDelete={handleOpenDeleteModal}
                    onDuplicate={handleDuplicate}
                    selectedIds={selectedIds}
                    onToggleSelection={toggleSelection}
                    onToggleAll={() => toggleAllSelection(filteredReasons)}
                />
            </section>
            <Pagination
                className="mt-s3"
                currentPage={currentPage}
                totalCount={filteredReasons.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                selectedCount={selectedIds.size}
            />

            <LossReasonFormModal
                isOpen={isFormModalOpen}
                onClose={() => setIsFormModalOpen(false)}
                onSave={handleSaveReason}
                reasonToEdit={reasonToEdit}
            />

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Excluir Motivo de Perda"
                message={`Tem certeza que deseja excluir o motivo "${reasonToDelete?.name}"?`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <ConfirmationModal
                isOpen={isBulkDeleteModalOpen}
                onClose={() => setIsBulkDeleteModalOpen(false)}
                onConfirm={handleBulkDelete}
                title="Excluir Motivos de Perda"
                message={`Tem certeza que deseja excluir ${selectedIds.size} motivo(s)? Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <BulkActionsBar
                selectedCount={selectedIds.size}
                actions={bulkActions}
                onClose={clearSelection}
            />
        </div>
    );
};

export default LossReasonsSettings;
